import React, { useCallback } from "react";
import styled from "styled-components";
import {
  EthereumWallet,
  SolanaWallet,
  Wallet,
  WALLET_TITLES,
} from "shared/lib/models/wallets";
import { ConnectorButtonProps } from "./types";
import { ConnectorButtonStatus } from "./types";
import {
  BaseButton,
  BaseIndicator,
  BaseModalContentColumn,
  Title,
} from "shared/lib/designSystem";
import useLoadingText from "shared/lib/hooks/useLoadingText";
import colors from "shared/lib/designSystem/colors";
import theme from "shared/lib/designSystem/theme";
import { BackIcon } from "shared/lib/assets/icons/icons";
import WalletLogo from "shared/lib/components/Wallet/WalletLogo";

const ModalContainer = styled.div`
  padding: 10px 0px;
`;

const TitleContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 24px;
  background: #10312F;
  width: 100%;
  height: 102px;
  font-family: 'MuseoModerno';
  font-style: normal;
  font-weight: 600;
  font-size: 24px;
  line-height: 38px;
  text-align: center;
  color: #ffffff;
`;

const BackButton = styled(BaseButton)`
  position: absolute;
  left: 16px;
  padding: 0;
  border: ${theme.border.width} ${theme.border.style} ${colors.border};
  color: ${colors.text};
  align-items: center;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  justify-content: center;
  cursor: pointer;
  opacity: ${theme.hover.opacity};
  transition: 0.2s;

  &:hover {
    opacity: 1;
  }
`;

const ConnectorButton = styled(BaseButton)<ConnectorButtonProps>`
  align-items: center;
  width: calc(100% - 64px);
  padding: 8px;
  padding-right: 16px;
  height: 56px;
  border: 1px solid rgba(226,248,241,0.3);

  &:hover {
    opacity: ${theme.hover.opacity};
  }

  ${(props) => {
    switch (props.status) {
      case "connected":
        return `
          border: 1px solid #E2F8F1;
        `;
      case "neglected":
        return `
          border: 1px solid #E2F8F1;

          &:hover {
            opacity: 0.24;
          }
        `;
      case "initializing":
        return `
          border: 1px solid #E2F8F1;

          &:hover {
            opacity: 1;
          }
        `;
      default:
        return ``;
    }
  }}
`;

const StyledBaseIndicator = styled(BaseIndicator)`
  margin-left: auto;
`;

const ConnectorButtonText = styled(Title)`
  margin-left: 16px;
`;

const ConnectorButtonPill = styled(ConnectorButton)`
  margin-bottom: 16px;
`;

const ConnectWalletBody: React.FC<{
  onSelectWallet: (wallet: Wallet) => void;
  onBack: () => void;
  selectedWallet: Wallet | undefined;
  connectingWallet: Wallet | undefined;
  wallets: Wallet[];
}> = ({
  onSelectWallet,
  onBack,
  selectedWallet,
  connectingWallet,
  wallets,
}) => {
  const getWalletStatus = useCallback(
    (wallet: Wallet): ConnectorButtonStatus => {
      if (selectedWallet === wallet) {
        return "connected";
      }

      if (!connectingWallet) {
        return "normal";
      } else if (connectingWallet === wallet) {
        return "initializing";
      }

      return "neglected";
    },
    [selectedWallet, connectingWallet]
  );

  return (
    <ModalContainer>
      <TitleContainer>
        <BackButton onClick={() => onBack()}>
          <BackIcon />
        </BackButton>
        <Title>Connect Wallet</Title>
      </TitleContainer>
      {wallets.map((wallet: Wallet, index: number) => {
        return (
          <BaseModalContentColumn key={index} marginTop={0}>
            <WalletButton
              wallet={wallet as Wallet}
              status={getWalletStatus(wallet as Wallet)}
              onConnect={async () => onSelectWallet(wallet as Wallet)}
            ></WalletButton>
          </BaseModalContentColumn>
        );
      })}
    </ModalContainer>
  );
};

export default ConnectWalletBody;

interface WalletButtonProps {
  wallet: Wallet;
  status: ConnectorButtonStatus;
  onConnect: () => Promise<void>;
}

const WalletButton: React.FC<WalletButtonProps> = ({
  wallet,
  status,
  onConnect,
}) => {
  const initializingText = useLoadingText("Initializing");
  const walletTitle = WALLET_TITLES[wallet];
  const walletColors = {
    [EthereumWallet.Metamask]: colors.wallets.Metamask,
    // [EthereumWallet.WalletConnect]: colors.wallets.WalletConnect,
    // [EthereumWallet.WalletLink]: colors.wallets.WalletLink,
    [SolanaWallet.Phantom]: colors.wallets.Phantom,
    [SolanaWallet.Solflare]: colors.wallets.Solflare,
  };

  return (
    <ConnectorButtonPill
      role="button"
      onClick={onConnect}
      status={status}
      color={walletColors[wallet]}
    >
      <WalletLogo wallet={wallet} />
      <ConnectorButtonText>
        {status === "initializing" ? initializingText : walletTitle}
      </ConnectorButtonText>
      {status === "connected" && (
        <StyledBaseIndicator
          size={8}
          color={walletColors[wallet]}
        ></StyledBaseIndicator>
      )}
    </ConnectorButtonPill>
  );
};
